import { useEffect, useRef, useState } from "react"

// Loaded from cdnjs at runtime (not an npm dependency) so no build step is
// required. This is the legacy (non-module) build of pdf.js, which exposes
// a plain `window.pdfjsLib` global - it renders each page to a <canvas>,
// so page-by-page navigation works the same on mobile as on desktop,
// unlike an <iframe src="*.pdf">, which mobile browsers often can't display inline.
const PDFJS_VERSION = "2.16.105"
const PDFJS_BASE = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}`
const PDFJS_SCRIPT_URL = `${PDFJS_BASE}/pdf.min.js`
const PDFJS_WORKER_URL = `${PDFJS_BASE}/pdf.worker.min.js`

let pdfjsLoadPromise = null

function loadPdfJs() {
    if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib)

    if (!pdfjsLoadPromise) {
        pdfjsLoadPromise = new Promise((resolve, reject) => {
            const script = document.createElement("script")
            script.src = PDFJS_SCRIPT_URL
            script.async = true
            script.onload = () => {
                window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL
                resolve(window.pdfjsLib)
            }
            script.onerror = () => reject(new Error("Failed to load the PDF viewer library"))
            document.body.appendChild(script)
        })
    }

    return pdfjsLoadPromise
}

export default function PdfPageViewer({ src, fallbackHref }) {
    const canvasRef = useRef(null)
    const containerRef = useRef(null)
    const pdfDocRef = useRef(null)
    const [pageNum, setPageNum] = useState(1)
    const [numPages, setNumPages] = useState(null)
    const [status, setStatus] = useState("loading") // loading | ready | error
    const [renderTick, setRenderTick] = useState(0)

    // Load the PDF document whenever the source changes
    useEffect(() => {
        let cancelled = false
        setStatus("loading")

        loadPdfJs()
            .then((pdfjsLib) => pdfjsLib.getDocument(src).promise)
            .then((pdfDoc) => {
                if (cancelled) return
                pdfDocRef.current = pdfDoc
                setNumPages(pdfDoc.numPages)
                setPageNum(1)
                setStatus("ready")
            })
            .catch((err) => {
                console.error("Failed to load PDF", err)
                if (!cancelled) setStatus("error")
            })

        return () => {
            cancelled = true
        }
    }, [src])

    // Re-render the current slide if the viewer's width changes (e.g. phone rotation)
    useEffect(() => {
        if (status !== "ready") return undefined

        let resizeTimer = null
        function handleResize() {
            clearTimeout(resizeTimer)
            resizeTimer = setTimeout(() => setRenderTick((tick) => tick + 1), 200)
        }

        window.addEventListener("resize", handleResize)
        return () => {
            clearTimeout(resizeTimer)
            window.removeEventListener("resize", handleResize)
        }
    }, [status])

    // Render the current page onto the canvas, scaled to fit the container's width
    useEffect(() => {
        if (status !== "ready" || !pdfDocRef.current) return undefined
        let cancelled = false

        async function renderPage() {
            const page = await pdfDocRef.current.getPage(pageNum)
            if (cancelled) return

            const containerWidth = containerRef.current
                ? containerRef.current.clientWidth
                : 800
            const unscaledViewport = page.getViewport({ scale: 1 })
            const scale = containerWidth / unscaledViewport.width
            const viewport = page.getViewport({ scale })

            const canvas = canvasRef.current
            if (!canvas) return
            const context = canvas.getContext("2d")
            canvas.width = viewport.width
            canvas.height = viewport.height

            await page.render({ canvasContext: context, viewport }).promise
        }

        renderPage()

        return () => {
            cancelled = true
        }
    }, [status, pageNum, renderTick])

    function goPrev() {
        setPageNum((p) => Math.max(1, p - 1))
    }

    function goNext() {
        setPageNum((p) => Math.min(numPages || p, p + 1))
    }

    if (status === "error") {
        return (
            <div className="saber-pdf-fallback">
                <p>The slide viewer couldn't load.</p>
                <a href={fallbackHref || src} target="_blank" rel="noopener noreferrer">
                    Open the PDF in a new tab
                </a>
            </div>
        )
    }

    return (
        <div className="saber-pdf-viewer" ref={containerRef}>
            {status === "loading" && <p className="saber-pdf-status">Loading slides…</p>}

            <canvas ref={canvasRef} className="saber-pdf-canvas" />

            {status === "ready" && (
                <div className="saber-pdf-controls">
                    <button type="button" onClick={goPrev} disabled={pageNum <= 1}>
                        Prev
                    </button>
                    <span className="saber-pdf-page-indicator">
                        Slide {pageNum} of {numPages}
                    </span>
                    <button type="button" onClick={goNext} disabled={pageNum >= numPages}>
                        Next
                    </button>
                </div>
            )}
        </div>
    )
}
